import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { toast } from "sonner";
import { naira } from "../../lib/format";
import { Plus, Trash2, Send } from "lucide-react";

const BURG = { background: "#8B1E1E" };
const blank = () => ({ product_id: "", quantity: 1, unit_price: 0 });

export default function BranchOrderDialog({ open, onClose, onSaved, products, suppliers }) {
  const [supplierId, setSupplierId] = useState("");
  const [reason, setReason] = useState("");
  const [lines, setLines] = useState([blank()]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSupplierId(suppliers[0]?.id || ""); setReason(""); setLines([blank()]);
  }, [open, suppliers]);

  const setLine = (i, k, v) => setLines((ls) => ls.map((l, idx) => idx === i ? { ...l, [k]: v } : l));
  const pickProduct = (i, pid) => {
    const p = products.find((x) => x.id === pid);
    setLines((ls) => ls.map((l, idx) => idx === i ? { ...l, product_id: pid, unit_price: l.unit_price || p?.cost || 0 } : l));
  };
  const total = lines.reduce((s, l) => s + Number(l.quantity || 0) * Number(l.unit_price || 0), 0);

  const save = async () => {
    const items = lines
      .filter((l) => l.product_id && Number(l.quantity) > 0)
      .map((l) => ({
        product_id: l.product_id,
        name: products.find((p) => p.id === l.product_id)?.name || "",
        quantity: Number(l.quantity),
        unit_price: Number(l.unit_price),
      }));
    if (!supplierId) { toast.error("Pick a supplier"); return; }
    if (!items.length) { toast.error("Add at least one item"); return; }
    setSaving(true);
    try {
      const bo = await api.post("/procurement/branch-orders", { supplier_id: supplierId, reason, items });
      toast.success(`${bo.number} raised — awaiting Operations Manager approval`);
      onSaved(); onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="bo-dialog">
        <DialogHeader>
          <DialogTitle className="font-brand" style={{ color: "#8B1E1E" }}>Raise a Branch Purchase Order</DialogTitle>
          <DialogDescription>For items your branch needs that aren't on the central plan. Requires Operations Manager sign-off before stock can be received.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label>Supplier</Label>
              <Select value={supplierId} onValueChange={setSupplierId}>
                <SelectTrigger data-testid="bo-supplier"><SelectValue placeholder="Select supplier" /></SelectTrigger>
                <SelectContent>{suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Reason</Label>
              <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why do you need this?" data-testid="bo-reason" />
            </div>
          </div>
          <div className="rounded-2xl border p-3" style={{ background: "#FFFDF9", borderColor: "#E9DED0" }}>
            <div className="flex items-center justify-between mb-2">
              <div className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: "#765A4A" }}>Items</div>
              <Button size="sm" variant="outline" onClick={() => setLines((ls) => [...ls, blank()])} data-testid="bo-add-line"><Plus size={13} className="mr-1" /> Add item</Button>
            </div>
            <div className="space-y-2">
              {lines.map((l, i) => (
                <div key={i} className="grid grid-cols-[1fr_90px_110px_36px] gap-2 items-center">
                  <Select value={l.product_id} onValueChange={(v) => pickProduct(i, v)}>
                    <SelectTrigger data-testid={`bo-line-product-${i}`}><SelectValue placeholder="Item" /></SelectTrigger>
                    <SelectContent>{products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input type="number" min="0" value={l.quantity} onChange={(e) => setLine(i, "quantity", e.target.value)} data-testid={`bo-line-qty-${i}`} />
                  <Input type="number" min="0" value={l.unit_price} onChange={(e) => setLine(i, "unit_price", e.target.value)} data-testid={`bo-line-price-${i}`} />
                  <button onClick={() => setLines((ls) => ls.length > 1 ? ls.filter((_, idx) => idx !== i) : ls)} className="p-1.5 rounded-lg hover:bg-[#F3ECE2]" data-testid={`bo-line-del-${i}`}>
                    <Trash2 size={15} style={{ color: "#A52222" }} />
                  </button>
                </div>
              ))}
            </div>
            <div className="pt-2 mt-2 border-t flex justify-between text-sm" style={{ borderColor: "#E9DED0" }}>
              <span style={{ color: "#765A4A" }}>Total</span>
              <span className="font-semibold" style={{ color: "#3B2722" }}>{naira(total)}</span>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="bo-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} style={BURG} className="text-white" data-testid="bo-save">
            <Send size={15} className="mr-1" /> {saving ? "Submitting…" : "Submit for Approval"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
